import {Injectable} from "@angular/core";
import {UserManagementService} from "../../user-managment/user-management.service";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import * as workspaceAction from "../actions/workspace.action";
import * as workspaceActions from "../actions/workspace.action";
import {filter, first, map, switchMap} from "rxjs/operators";
import {DbResponse} from "../../shared/interceptors/db-response";
import {WorkspaceMapper} from "../../user-managment/workspace.mapper";
import {WorkspaceService} from "../../workspace/workspace.service";
import * as activityActions from "../actions/activities.action";
import {AddActivity} from "../actions/activities.action";
import {ActivityAction, ActivityStatus} from "../../activities/activity";
import * as websiteActions from "../actions/website.action";
import * as userActions from "../actions/user.action";
import {
  GlobalEventDispatcherService
} from "../../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import {Router} from "@angular/router";
import * as fromStore from "../../store";
import {Store} from '@ngrx/store';
import {combineLatest} from "rxjs";
import {User} from "../../user/user";
import {Workspace} from "../../user-managment/workspace";

@Injectable()
export class WorkspaceEffects {

  loadWorkspaceClients = createEffect(() => this._actions$.pipe(
    ofType(workspaceAction.LOAD_WORKSPACE_ALL_CLIENTS),
    switchMap(() => {
      return this._userManagementService.getWorkspaceClients()
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              const clients = WorkspaceMapper.workspaceClientsMap(response.data.clients);
              const invitations = WorkspaceMapper.workspaceInvitationsMap(response.data.invitations);
              return new workspaceAction.LoadWorkspaceAllClientsSuccess({clients: clients, invitations: invitations});
            } else {
              return new workspaceAction.LoadWorkspaceAllClientsFail(response.message);
            }
          }),
          first()
        );
    })
  ));

  loadBillingInfo = createEffect(() => this._actions$.pipe(
    ofType(workspaceAction.LOAD_BILLING_INFO),
    switchMap(() => {
      return this._workspaceService.getBillingInfo()
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              return new workspaceAction.LoadBillingInfoSuccess(response.data);
            } else {
              return new workspaceAction.LoadBillingInfoFail(response.message);
            }
          }),
          first()
        );
    })
  ));

  agreementStateChanges$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => [ActivityAction.AGREEMENT_ACTIVATE, ActivityAction.AGREEMENT_RENEW, ActivityAction.AGREEMENT_UPGRADE].includes(<any>action.payload.action) && action.payload.status === ActivityStatus.DONE),
    map((action: AddActivity) => new userActions.LoadUserSubscription())
  ));
  private currentWorkspaceId: number;
  workspaceInvitationAccept$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => action.payload && [ActivityAction.WORKSPACE_INVITATION_ACCEPT].includes(<any>action.payload.action)
      && action.payload.status === ActivityStatus.DONE && action.payload.meta && action.payload.meta.current_workspace_id
      && this.currentWorkspaceId === action.payload.meta.current_workspace_id),
    map((action: AddActivity) => new workspaceActions.LoadWorkspaceAllClients())
  ));
  private ownerWorkspaceId: any;
  private ownWorkspace: any;
  private user: User;
  workspaceRoleEdit$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => (<string[]>[ActivityAction.WORKSPACE_ROLE_EDIT, ActivityAction.WORKSPACE_LEAVE, ActivityAction.WORKSPACE_CLIENT_REMOVE]).includes(<any>action.payload.action)),
    switchMap((action: AddActivity) => {
      localStorage.removeItem('dbPreviousWorkspaceId');
      localStorage.removeItem('dbPreviousWorkspaceRoleId');
      const otherWorkspace: Workspace = this.user.workspaces.find(el => el.workspaceId !== this.ownWorkspace.workspaceId && el.workspaceId !== action.payload.meta.current_workspace_id);
      const redirectionWorkspace: Workspace = this.ownWorkspace && !['invited'].includes(this.ownWorkspace.status) ? this.ownWorkspace : otherWorkspace ? otherWorkspace : this.ownWorkspace;
      if (this.user && action.payload.meta && !otherWorkspace
        && ['invited'].includes(this.ownWorkspace?.status)) {
        this._router.navigate(['/upgrade-plan']);
      } else if (action.payload.meta && this.currentWorkspaceId === action.payload.meta.current_workspace_id) {
        this._router.navigate(['/websites']);
      }
      return [
        new workspaceActions.SetWorkspaceIdEntities({
          ownerWorkspaceId: this.ownerWorkspaceId,
          currentWorkspaceId: action.payload.action === ActivityAction.WORKSPACE_ROLE_EDIT ? this.currentWorkspaceId : redirectionWorkspace.workspaceId,
          currentWorkspaceRoleId: action.payload.action === ActivityAction.WORKSPACE_ROLE_EDIT ? action.payload.meta.role_id : redirectionWorkspace.roleId,
          globalEventDispatcherService: this._globalEventDispatcher
        }),
        new userActions.LoadUser(),
        new userActions.LoadUserSubscription(),
        new userActions.LoadUserReferralSubscription(),
        new userActions.LoadUserAvailableSubscriptions(),
        new workspaceActions.LoadWorkspaceAllClients(),
        new websiteActions.ClearWebsites(),
        new websiteActions.LoadWebsites(),
      ];
    })
  ));

  constructor(
    private _actions$: Actions,
    private _userManagementService: UserManagementService,
    private _workspaceService: WorkspaceService,
    private _globalEventDispatcher: GlobalEventDispatcherService,
    private _router: Router,
    private _store: Store<fromStore.DbStoreState>
  ) {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getOwnerWorkspace),
          this._store.select(fromStore.getWorkspaceIdEntities)
        ))
      )
      .subscribe(([user, ownWorkspace, workspaceIdEntities]) => {
        this.user = user;
        this.ownWorkspace = ownWorkspace;
        this.ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
        this.currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this.ownerWorkspaceId;
      });
  }
}
